import { useCallback, useMemo } from "react"
import { toast } from "react-toastify"

import {
  useGetCustomConnectorPropertiesQuery,
  useCompleteCustomConnectorSetupMutation,
  type CustomConnectorConfigField,
} from "~/ui-rtk/api/customConnectorApi"
import { ConnectorDto } from "~/ui-rtk/api/types"
import { hasErrorMessage } from "~/ui-rtk/utils/http-utils"

// Module-level constant so the "no schema" return shape uses the same array
// reference across renders — prevents downstream useMemo/useEffect re-runs
// from a fresh `?? []` allocation each render.
const EMPTY_SCHEMA: CustomConnectorConfigField[] = []

/**
 * Parallel to useAirbyteAdditionalConfig but for custom (non-Airbyte) connectors
 * that declare a `customAdditionalConfig` schema on their service class. Values
 * land in `credentials.metadata.*` rather than an Airbyte source configuration.
 */
export const useCustomConnectorAdditionalConfig = (
  companyConnectorId: string,
  connector?: ConnectorDto,
) => {
  const {
    data: properties,
    isLoading: isLoadingProperties,
    error: propertiesError,
  } = useGetCustomConnectorPropertiesQuery(
    { id: companyConnectorId },
    { skip: !companyConnectorId },
  )

  const [completeCustomSetup, { isLoading: isCompletingSetup }] =
    useCompleteCustomConnectorSetupMutation()

  const completeSetup = useCallback(
    async (additionalConfig: Record<string, unknown>) => {
      if (!companyConnectorId) {
        throw new Error("No company connector ID provided")
      }

      try {
        await completeCustomSetup({
          id: companyConnectorId,
          additionalConfig,
        }).unwrap()

        toast.success(`${connector?.name ?? "Connector"} setup complete.`, {
          toastId: `custom-setup-success-${companyConnectorId}`,
        })
      } catch (error: unknown) {
        let errorMessage = `Failed to complete ${connector?.name ?? "connector"} setup`

        if (hasErrorMessage(error)) {
          // Toast shows only the short message. The structured errors[]
          // detail is rendered inline (persistent, actionable) by
          // CustomConnectorConfigForm — joining it into the toast too
          // would surface the exact same strings twice on screen.
          errorMessage = error.data.message
        } else if (error instanceof Error) {
          errorMessage = error.message
        }

        toast.error(errorMessage, {
          toastId: `custom-setup-error-${companyConnectorId}`,
        })
        throw error
      }
    },
    [companyConnectorId, connector, completeCustomSetup],
  )

  // Stable references across renders so consumers' useMemo/useEffect
  // dependency arrays don't fire on every render. RTK Query returns the
  // same `properties` reference until the data actually changes, so
  // wrapping the destructured fields keeps both real values and "empty"
  // fallbacks reference-stable.
  const schema = useMemo(
    () => properties?.additional_config ?? EMPTY_SCHEMA,
    [properties],
  )
  const existingMetadata = useMemo(
    () => properties?.metadata ?? null,
    [properties],
  )

  return {
    schema,
    existingMetadata,
    isLoadingProperties,
    propertiesError: propertiesError ? "Failed to load configuration" : null,
    completeSetup,
    isCompletingSetup,
  }
}
